import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticleViewService from "@core-ui/ContextServices/views/articleView/ArticleViewService";
import useSetArticleDiffView from "@core-ui/hooks/diff/useSetArticleDiffView";
import { useScrollPositionStore } from "@core-ui/stores/ScrollPositionStore";
import { useDiffExtendedMode } from "@ext/git/core/Diff/components/store/DiffExtendedModeStore";
import { setDiffEnabled } from "@ext/git/core/Diff/components/store/DiffViewModeStore";
import { useDiffSwitch } from "@ext/git/core/Diff/logic/hooks/useDiffToggle";
import { useLeaveDiffView } from "@ext/git/core/Diff/logic/hooks/useLeaveDiffView";
import { countSelectedVisibleEntries, isDiffEntryVisible } from "@ext/git/core/Diff/logic/utils/visibleDiffEntries";
import type { DiffFlattenTreeAnyItem } from "@ext/git/core/GitDiffItemCreator/RevisionDiffPresenter";
import { getDiscardPaths } from "@ext/git/core/GitPublish/logic/getDiscardPaths";
import { useDiscard } from "@ext/git/core/GitPublish/useDiscard";
import usePublish from "@ext/git/core/GitPublish/usePublish";
import usePublishDiffEntries from "@ext/git/core/GitPublish/usePublishDiffEntries";
import usePublishHealth from "@ext/git/core/GitPublish/usePublishHealth";
import usePublishSelection from "@ext/git/core/GitPublish/usePublishSelectedFiles";
import { useWaitPublishHealth } from "@ext/git/core/GitPublish/useWaitPublishHealth";
import t from "@ext/localization/locale/translate";
import useIsSourceDataValid from "@ext/storage/components/useIsSourceDataValid";
import {
	PanelEmptyState,
	PanelEmptyStateDescription,
	PanelEmptyStateIcon,
	PanelEmptyStateTitle,
	useFloatingPanelStore,
} from "@ui-kit/FloatingPanel";
import { Loader } from "@ui-kit/Loader";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PublishFooter } from "./components/PublishFooter";
import { PublishSelectAllRow } from "./components/PublishSelectAllRow";
import { PublishTree } from "./components/PublishTree";
import { PUBLISH_PANEL_ID } from "./constants";
import { useLeaveDeletedArticle } from "./useLeaveDeletedArticle";

export const PublishPanelContent = () => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const apiUrlCreatorRef = useRef(apiUrlCreator);
	apiUrlCreatorRef.current = apiUrlCreator;

	const [isDiscarding, setIsDiscarding] = useState(false);
	const [isWaitingHealth, setIsWaitingHealth] = useState(false);
	const [activePath, setActivePath] = useState<string>(null);

	const isOpen = useFloatingPanelStore((state) => !!state.panels[PUBLISH_PANEL_ID]?.isOpen);
	const setIsOpen = useFloatingPanelStore((state) => state.setIsOpen);
	const clearAllPositions = useScrollPositionStore((state) => state.clearAll);
	const extendedMode = useDiffExtendedMode();
	const { enable: enableDiff, disable: disableDiff, isOn: diffOn, isCurrentArticle } = useDiffSwitch();
	const diffSwitchRef = useRef({ enableDiff, disableDiff });
	diffSwitchRef.current = { enableDiff, disableDiff };
	const diffOnRef = useRef(diffOn);
	diffOnRef.current = diffOn;
	const setArticleDiffView = useSetArticleDiffView(null, "HEAD");
	const canPush = useIsSourceDataValid();

	const { status: publishHealthStatus, isProtectedBranch, hasGitConflicts } = usePublishHealth(canPush);

	const { diffTree, overview, isEntriesLoading, isEntriesReady } = usePublishDiffEntries({ autoUpdate: isOpen });
	const { selectedFiles, isSelectedAll, selectFile, selectAll, isSelected, resetSelection } = usePublishSelection({
		diffTree,
	});

	const onChangesClear = useCallback(() => {
		resetSelection();
		clearAllPositions();
		setIsOpen(PUBLISH_PANEL_ID, false);
	}, [resetSelection, clearAllPositions, setIsOpen]);

	const leaveDiffView = useLeaveDiffView();
	const onPublished = useCallback(() => {
		onChangesClear();
		leaveDiffView();
	}, [onChangesClear, leaveDiffView]);

	const { isPublishing, message, publish, setMessage } = usePublish({
		diffTree,
		selectedFiles,
		onPublished,
	});

	const { discard } = useDiscard();

	const { leave: leaveDeletedArticle, noteDiscard } = useLeaveDeletedArticle(isOpen);

	useEffect(() => {
		if (!isOpen) return;
		const enabledHere = !diffOnRef.current;
		if (enabledHere) diffSwitchRef.current.enableDiff();

		return () => {
			if (leaveDeletedArticle(!enabledHere)) {
				if (!enabledHere) return;
				ArticleViewService.setDefaultView();
				setDiffEnabled(false);
				return;
			}
			if (!enabledHere) return;
			const wasDiffView = !ArticleViewService.isDefaultView();
			ArticleViewService.setDefaultView();
			const reloaded = diffSwitchRef.current.disableDiff();
			if (reloaded || !wasDiffView) return;

			void ArticleUpdaterService.update(apiUrlCreatorRef.current).then(() => refreshPage());
		};
	}, [isOpen, leaveDeletedArticle]);

	const discardPaths = useCallback(
		async (paths?: string[]) => {
			setIsDiscarding(true);
			const discarded = paths || Array.from(selectedFiles);
			noteDiscard(discarded);
			await discard(discarded, !paths);
			setIsDiscarding(false);

			if (!paths) return onChangesClear();
		},
		[discard, selectedFiles, onChangesClear, noteDiscard],
	);

	const onDiscardItem = useCallback(
		async (item: DiffFlattenTreeAnyItem) => {
			if (item.type === "node") return;
			await discardPaths(getDiscardPaths(item));
		},
		[discardPaths],
	);

	const onSelectItem = useCallback(
		(item: DiffFlattenTreeAnyItem, checked: boolean) => {
			if (item.type === "node") return;
			if (isSelectedAll) selectAll(checked);
			selectFile(item.filepath.new, checked, item.filepath.old);
		},
		[isSelectedAll, selectAll, selectFile],
	);

	const isItemSelected = useCallback(
		(item: DiffFlattenTreeAnyItem) => {
			if (item.type === "node") return false;
			return isSelected(item.filepath.new, item.filepath.old);
		},
		[isSelected],
	);

	const onOpenItem = useCallback(
		(item: DiffFlattenTreeAnyItem) => {
			if (item.type === "node") return;
			setActivePath(item.filepath.new);
			if (item.type === "item" && isCurrentArticle(item.filepath.new)) {
				enableDiff();
				return;
			}
			setArticleDiffView(item);
		},
		[setArticleDiffView, isCurrentArticle, enableDiff],
	);

	const items = useMemo(
		() => (diffTree?.data ?? []).filter((item) => isDiffEntryVisible(item, extendedMode)),
		[diffTree, extendedMode],
	);
	const currentContent = { items, overview, message };
	const [publishingContent, setPublishingContent] = useState<typeof currentContent>(null);
	const lastOpenContentRef = useRef(currentContent);

	if (isOpen && !publishingContent && !isPublishing) lastOpenContentRef.current = currentContent;
	const visibleContent = publishingContent ?? (isOpen && !isPublishing ? currentContent : lastOpenContentRef.current);

	useLayoutEffect(() => {
		if (isOpen && !isPublishing) setPublishingContent(null);
	}, [isOpen, isPublishing]);

	const waitPublishHealth = useWaitPublishHealth(publishHealthStatus);
	// the selection may change while the click waits for the healthcheck: publish the one current after the wait
	const latestContentRef = useRef({ items, overview, message, publish });
	latestContentRef.current = { items, overview, message, publish };

	const publishCurrentContent = useCallback(async () => {
		// the healthcheck fetch must not overlap with the publish sync fetch (ref lock error)
		setIsWaitingHealth(true);
		await waitPublishHealth();
		setIsWaitingHealth(false);

		const { publish, ...content } = latestContentRef.current;
		setPublishingContent(content);
		if (!(await publish())) setPublishingContent(null);
	}, [waitPublishHealth]);

	const isFileSelected = useCallback(
		(file: DiffFlattenTreeAnyItem) => {
			if (file.type === "node") return false;
			return isSelected(file.filepath.new, file.filepath.old);
		},
		[isSelected],
	);

	const fileCount = useMemo(
		() => countSelectedVisibleEntries(visibleContent.items, extendedMode, isFileSelected),
		[visibleContent.items, extendedMode, isFileSelected],
	);

	// waiting for the healthcheck on click is already part of publishing for the user
	const isPublishBusy = isPublishing || isWaitingHealth;

	const hasChanges = visibleContent.items.length > 0;
	const isTreeLoading = !diffTree?.data && isEntriesLoading;
	const canDiscard = selectedFiles.size > 0 && !isPublishBusy && !isEntriesLoading && isEntriesReady;
	const selectedFilesReady = selectedFiles.size > 0 && isEntriesReady && !isEntriesLoading;
	// While something is running the button carries a spinner and the reason is visible without words;
	// an empty selection is the state nothing on screen explains.
	const publishDisabledReason =
		!isDiscarding && !isPublishBusy && selectedFiles.size === 0
			? t("disabled-reason.publish-no-selection")
			: undefined;

	if (isTreeLoading) return <Loader className="py-6" size="3xl" />;

	if (!hasChanges)
		return (
			<PanelEmptyState>
				<PanelEmptyStateIcon icon="circle-check" />
				<PanelEmptyStateTitle>{t("git.publish.empty-state.title")}</PanelEmptyStateTitle>
				<PanelEmptyStateDescription className="max-w-64">
					{t("git.publish.empty-state.description")}
				</PanelEmptyStateDescription>
			</PanelEmptyState>
		);

	return (
		<div className="flex min-h-0 flex-1 flex-col px-2 pb-2">
			<div className="mb-2">
				<PublishSelectAllRow
					canDiscard={canDiscard}
					discardCount={selectedFiles.size}
					isSelectedAll={isSelectedAll}
					onDiscardAll={() => discardPaths()}
					onSelectAll={selectAll}
					overview={visibleContent.overview}
				/>
			</div>
			<ScrollShadowContainer className="flex min-h-0 flex-1 flex-col" style={{ scrollbarWidth: "none" }}>
				<PublishTree
					activePath={activePath}
					canDiscard={canDiscard}
					entries={visibleContent.items}
					isSelected={isItemSelected}
					onDiscard={onDiscardItem}
					onOpen={onOpenItem}
					onSelect={onSelectItem}
				/>
			</ScrollShadowContainer>
			<PublishFooter
				fileCount={fileCount}
				hasGitConflicts={hasGitConflicts}
				isInputDisabled={
					isProtectedBranch || isDiscarding || isPublishBusy || !isEntriesReady || hasGitConflicts
				}
				isProtectedBranch={isProtectedBranch}
				isPublishDisabled={isDiscarding || isPublishBusy || !selectedFilesReady}
				isPublishing={isPublishBusy}
				message={visibleContent.message}
				onMessageChange={setMessage}
				onPublish={() => void publishCurrentContent()}
				publishDisabledReason={publishDisabledReason}
			/>
		</div>
	);
};
